import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert, Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  AtsGetNewCandidateEmployeesDocument,
  CreateCandidatesFromEmployeesDocument,
  EmployeeForSelectFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { joinAsSentence } from 'modules/core/lib/stringUtils'
import app from 'modules/core/store/models/app'

import { useCandidates } from 'modules/ats/hooks/candidates/useCandidates'
import { EntitySelectorWrapper } from 'modules/employees/components/EmployeeSelector/EntitySelectorWrapper'

const normalizeEmail = (email?: string | null): string => {
  if (!email) return ''
  return email.trim().toLowerCase()
}

const useDuplicatedCandidates = (employeeIds: number[]) => {
  const skip = employeeIds.length === 0

  const { data: employeesData, loading: employeesLoading } = useQuery(
    AtsGetNewCandidateEmployeesDocument,
    {
      variables: { ids: employeeIds },
      fetchPolicy: 'network-only',
      skip,
    }
  )

  const employeesEmails = useUnsafeMemo(() => {
    const employees = employeesData?.employees?.employeesConnection?.nodes ?? []

    return employees.map((e) => normalizeEmail(e.email))
  }, [employeesData])

  const {
    loading: candidatesLoading,
    candidates,
    previousCandidates,
  } = useCandidates({
    variables: { emails: employeesEmails },
    skip: employeesEmails.length === 0,
    fetchPolicy: 'cache-and-network',
  })

  const allCandidates = candidates || previousCandidates || []

  return {
    loading: employeesLoading || candidatesLoading,
    candidatesAlreadyAdded: allCandidates,
  }
}

const NewCandidateFromEmployeePage = () => {
  const { company } = useCurrentSession()
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState<number[]>([])
  const [selectedEmployees, setSelectedEmployees] = React.useState<EmployeeForSelectFragment[]>([])
  const [createCandidatesFromEmployees, { loading: creating }] = useMutation(
    CreateCandidatesFromEmployeesDocument,
    {
      extractorFn: (data) => data.atsMutations.createFromEmployeesAtsCandidate,
    }
  )
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { loading, candidatesAlreadyAdded } = useDuplicatedCandidates(selectedEmployeeIds)

  const noSelectedEmployees = selectedEmployeeIds.length === 0
  const hasDuplicatedCandidates = candidatesAlreadyAdded.length > 0

  const onSelectEmployees = (employees: EmployeeForSelectFragment[]) => {
    setSelectedEmployees(employees)
    setSelectedEmployeeIds(employees.map((employee) => employee.id))
  }

  const navigateToCandidates = () => navigate(navigation.ats.candidates.fullPath())

  const onSubmit = async () => {
    const result = await createCandidatesFromEmployees({
      variables: {
        companyId: company.id,
        employeesIds: selectedEmployeeIds,
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      navigateToCandidates()
    }

    return handled
  }

  return (
    <Modal
      illustration="people-add"
      title={i18n.t('ats.candidates.employee_candidate_modal.title')}
      description={i18n.t('ats.candidates.employee_candidate_modal.talent_pool_description')}
      onClose={navigateToCandidates}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={navigateToCandidates}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            submit
            disabled={creating || loading || noSelectedEmployees || hasDuplicatedCandidates}
          >
            {i18n.t('ats.candidates.employee_candidate_modal.save')}
          </Button>
        </>
      }
    >
      <Box gap="s16" width="s400">
        <EntitySelectorWrapper
          multiple
          usingAccessIds
          label={i18n.t('common.employees')}
          onSelect={onSelectEmployees}
          value={selectedEmployees}
        />

        {hasDuplicatedCandidates && (
          <Alert color="warning">
            <Text.Normal>
              {i18n.tp('ats.candidates.employee_candidate_modal.candidates_already_added_warning', {
                count: candidatesAlreadyAdded.length,
                candidateNames: joinAsSentence(
                  candidatesAlreadyAdded.map((candidate) => candidate.fullName)
                ),
              })}
            </Text.Normal>
          </Alert>
        )}
      </Box>
    </Modal>
  )
}

export default NewCandidateFromEmployeePage
